import { useQuery, useQueryClient, type QueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import {
  CONTACT_STATUS_LABELS,
  contactStatusAfterCall,
  type AddPlannerRowInput,
  type BulkActionRequest,
  type CallOutcome,
  type ContactRow,
  type ContactStatus,
  type ContactTrackerView,
  type InterviewSummaryView,
  type PipelineActionView,
  type PlannerView,
  type SetContactStatusInput,
  type StageBar,
} from '@nest-recruit/shared';
import { CellWarning } from '../../../components/editable-table/EditableTable';
import { apiGet, apiPost, apiPut, errorMessage, type ApiError } from '../../../lib/api-client';
import { useToasts } from '../../../stores/toasts';
import { RESULTS_KEY } from '../../assessments/api/assessments-api';
import { EXAM_SESSIONS_KEY } from '../../assessments/api/exam-sessions-api';
import { warningsOf } from '../../interviews/hooks/use-interview-actions';
import { stageBarKey } from '../../jobs/api/jobs-api';
import { shiftStageBar } from '../../jobs/stage-bar-shift';
import { startBulkAction } from '../../pipeline/api/pipeline-api';

// R7 — stage ⑥ Contact: the tracker (CON-01) and the planner (PLN-01, PLN-02). The tracker sits
// under `['applications']` and the planner under `['interviews']`, the keys the API's hints
// name for the job room, so a colleague's change reaches an open screen by itself (#12).

export function contactKey(jobId: string): readonly unknown[] {
  return ['applications', 'contact', jobId];
}

export function plannerKey(jobId: string): readonly unknown[] {
  return ['interviews', 'planner', jobId];
}

export function useContactTracker(jobId: string): UseQueryResult<ContactTrackerView, ApiError> {
  return useQuery<ContactTrackerView, ApiError>({
    queryKey: contactKey(jobId),
    queryFn: () => apiGet<ContactTrackerView>(`/jobs/${jobId}/contact`),
    placeholderData: (previous) => previous,
  });
}

export function usePlanner(jobId: string): UseQueryResult<PlannerView, ApiError> {
  return useQuery<PlannerView, ApiError>({
    queryKey: plannerKey(jobId),
    queryFn: () => apiGet<PlannerView>(`/jobs/${jobId}/planner`),
    placeholderData: (previous) => previous,
  });
}

/** Draws a change on one tracker row; returns the way back. */
function drawContact(queryClient: QueryClient, jobId: string, applicationId: string, patch: Partial<ContactRow>): () => void {
  const key = contactKey(jobId);
  const snapshot = queryClient.getQueryData<ContactTrackerView>(key);
  queryClient.setQueryData<ContactTrackerView>(key, (view) =>
    view ? { ...view, rows: view.rows.map((row) => (row.applicationId === applicationId ? { ...row, ...patch } : row)) } : view,
  );
  return () => queryClient.setQueryData(key, snapshot);
}

/** The server's copy has landed — only now does the optimistic state let go (§5a rule 3). */
async function settle(queryClient: QueryClient): Promise<void> {
  await Promise.all([queryClient.invalidateQueries({ queryKey: ['applications'] }), queryClient.invalidateQueries({ queryKey: ['interviews'] })]);
}

function usePending(): readonly [ReadonlySet<string>, (id: string, isOn: boolean) => void] {
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());
  const mark = useCallback((id: string, isOn: boolean) => {
    setPending((current) => {
      const next = new Set(current);
      if (isOn) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);
  return [pending, mark];
}

export type ContactChoice = Omit<SetContactStatusInput, 'version' | 'note' | 'withdrawalReasonCode'> & {
  readonly note?: string;
  readonly withdrawalReasonCode?: string | null;
};

/** F24.06: the status shows at once; a refusal puts it back and says why. */
export function useSetContactStatus(jobId: string): { readonly set: (row: ContactRow, choice: ContactChoice) => Promise<boolean>; readonly pending: ReadonlySet<string> } {
  const queryClient = useQueryClient();
  const show = useToasts((toasts) => toasts.show);
  const [pending, mark] = usePending();
  const set = useCallback(
    async (row: ContactRow, choice: ContactChoice): Promise<boolean> => {
      const restore = drawContact(queryClient, jobId, row.applicationId, { contactStatus: choice.to });
      mark(row.applicationId, true);
      try {
        await apiPut(`/applications/${row.applicationId}/contact-status`, {
          to: choice.to,
          note: choice.note ?? '',
          withdrawalReasonCode: choice.withdrawalReasonCode ?? null,
          version: row.version,
        });
        await settle(queryClient);
        return true;
      } catch (error) {
        restore();
        show({ tone: 'danger', message: `${row.candidateName}: not marked ${CONTACT_STATUS_LABELS[choice.to].toLowerCase()}`, detail: errorMessage(error) });
        return false;
      } finally {
        mark(row.applicationId, false);
      }
    },
    [queryClient, jobId, show, mark],
  );
  return { set, pending };
}

/** CT-3: a call's outcome in one tap. The call is logged; the server moves the status (§L32). */
export function useLogCall(jobId: string): { readonly log: (row: ContactRow, outcome: CallOutcome) => Promise<void>; readonly pending: ReadonlySet<string> } {
  const queryClient = useQueryClient();
  const show = useToasts((toasts) => toasts.show);
  const [pending, mark] = usePending();
  const log = useCallback(
    async (row: ContactRow, outcome: CallOutcome): Promise<void> => {
      const next: ContactStatus | null = contactStatusAfterCall(row.contactStatus, outcome);
      const restore = drawContact(queryClient, jobId, row.applicationId, {
        ...(next ? { contactStatus: next } : {}),
        attempts: outcome === 'reached' ? row.attempts : row.attempts + 1,
        lastContactAt: new Date().toISOString(),
      });
      mark(row.applicationId, true);
      try {
        // §L32: while Tasks is off, a logged call never asks for a next action.
        await apiPost('/activities', { applicationId: row.applicationId, type: 'call', outcome, noFollowUp: true });
        await settle(queryClient);
        show({ tone: 'success', message: `Call logged: ${row.candidateName}`, detail: next ? CONTACT_STATUS_LABELS[next] : undefined });
      } catch (error) {
        restore();
        show({ tone: 'danger', message: `${row.candidateName}: call not logged`, detail: errorMessage(error) });
      } finally {
        mark(row.applicationId, false);
      }
    },
    [queryClient, jobId, show, mark],
  );
  return { log, pending };
}

/** The tracker's moves into ⑦ Assessment: the link, a move alone (ADR 0047), or a seat (S18 Q2). */
export type ToAssessmentRequest = Extract<BulkActionRequest, { readonly action: 'assign_assessment' | 'book_exam_session' }>;

/**
 * F24.10 from the tracker: "Send assessment", "Move to Assessment" and "Add to exam session" run
 * the pipeline's own bulk action. The people show in Assessment at once — on their rows and on the
 * stage bar (§5a rule 1) — and stay so until the server's recount has landed (rule 3); a refusal
 * puts both back and throws, for the caller's toast. Before D9 nothing here refreshed the bar, so
 * it read "Contact 2 · Assessment 0" until a reload.
 */
export function useMoveToAssessment(jobId: string): (request: ToAssessmentRequest) => Promise<PipelineActionView> {
  const queryClient = useQueryClient();
  return useCallback(
    async (request: ToAssessmentRequest): Promise<PipelineActionView> => {
      const ids = new Set(request.selection.kind === 'ids' ? request.selection.ids : []);
      // A recount already on its way would land the old numbers over the drawn ones.
      await Promise.all([queryClient.cancelQueries({ queryKey: stageBarKey(jobId) }), queryClient.cancelQueries({ queryKey: contactKey(jobId) })]);
      const restore = drawToAssessment(queryClient, jobId, ids);
      try {
        const view = await startBulkAction(jobId, request);
        await Promise.all([settle(queryClient), queryClient.invalidateQueries({ queryKey: RESULTS_KEY }), queryClient.invalidateQueries({ queryKey: EXAM_SESSIONS_KEY })]);
        return view;
      } catch (error) {
        restore();
        throw error;
      }
    },
    [queryClient, jobId],
  );
}

/** Only the people still at ⑥ Contact move: one already in Assessment is not counted twice. */
function drawToAssessment(queryClient: QueryClient, jobId: string, ids: ReadonlySet<string>): () => void {
  const trackerKey = contactKey(jobId);
  const barKey = stageBarKey(jobId);
  const tracker = queryClient.getQueryData<ContactTrackerView>(trackerKey);
  const bar = queryClient.getQueryData<StageBar>(barKey);
  const moving = tracker?.rows.filter((row) => ids.has(row.applicationId) && row.stage === 'screening') ?? [];
  if (tracker) {
    queryClient.setQueryData<ContactTrackerView>(trackerKey, {
      ...tracker,
      rows: tracker.rows.map((row) => (moving.includes(row) ? { ...row, stage: 'assessment' } : row)),
    });
  }
  if (bar) queryClient.setQueryData<StageBar>(barKey, shiftStageBar(bar, 'contact', 'assessment', moving.length));
  return () => {
    if (tracker) queryClient.setQueryData(trackerKey, tracker);
    if (bar) queryClient.setQueryData(barKey, bar);
  };
}

// --- The planner's cells (F24.08) -----------------------------------------------------------

/** One cell's change, as the planner table hands it over. */
export type PlannerEdit =
  | { readonly kind: 'time'; readonly startsAt: string }
  | { readonly kind: 'panel'; readonly userIds: readonly string[] }
  | { readonly kind: 'where'; readonly mode: InterviewSummaryView['mode']; readonly meetingLink: string | null; readonly roomId: string | null }
  | { readonly kind: 'notes'; readonly notes: string };

function drawInterview(queryClient: QueryClient, jobId: string, next: InterviewSummaryView): () => void {
  const key = plannerKey(jobId);
  const snapshot = queryClient.getQueryData<PlannerView>(key);
  queryClient.setQueryData<PlannerView>(key, (view) => (view ? { ...view, rows: view.rows.map((row) => (row.id === next.id ? next : row)) } : view));
  return () => queryClient.setQueryData(key, snapshot);
}

/**
 * Each cell saves on its own (QoL §14), optimistically. A double booking comes back as a 409
 * with its warnings: the row goes back and the warning shows in the cell, unsaved, until
 * "Save anyway" sends it again confirmed.
 */
export function usePlannerSave(jobId: string): (interview: InterviewSummaryView, drawn: InterviewSummaryView, edit: PlannerEdit, isConfirmed: boolean) => Promise<void> {
  const queryClient = useQueryClient();
  const show = useToasts((toasts) => toasts.show);
  return useCallback(
    async (interview, drawn, edit, isConfirmed) => {
      const restore = drawInterview(queryClient, jobId, drawn);
      try {
        const base = `/interviews/${interview.id}`;
        if (edit.kind === 'time') await apiPut(`${base}/time`, { startsAt: edit.startsAt, version: interview.version, isConflictAccepted: isConfirmed });
        else if (edit.kind === 'panel') await apiPut(`${base}/panel`, { userIds: edit.userIds, version: interview.version, isConflictAccepted: isConfirmed });
        else if (edit.kind === 'where') await apiPut(`${base}/details`, { mode: edit.mode, meetingLink: edit.meetingLink, roomId: edit.roomId, version: interview.version });
        else await apiPut(`${base}/details`, { notes: edit.notes, version: interview.version });
        await settle(queryClient);
      } catch (error) {
        restore();
        const warnings = warningsOf(error);
        if (warnings && !isConfirmed) throw new CellWarning(warnings);
        show({ tone: 'danger', message: `${interview.candidateName}: not saved`, detail: errorMessage(error) });
        throw error;
      }
    },
    [queryClient, jobId, show],
  );
}

/** F24.08 "+ Add row": the candidate moves to Interview 1 and their row appears (DECISIONS-LOG "Start R7" Q1). */
export function useAddPlannerRow(jobId: string): { readonly add: (input: AddPlannerRowInput & { readonly candidateName: string }) => Promise<string | null>; readonly isAdding: boolean } {
  const queryClient = useQueryClient();
  const show = useToasts((toasts) => toasts.show);
  const [isAdding, setIsAdding] = useState(false);
  const add = useCallback(
    async (input: AddPlannerRowInput & { readonly candidateName: string }): Promise<string | null> => {
      setIsAdding(true);
      try {
        const created = await apiPost<{ readonly interviewId: string }>(`/jobs/${jobId}/planner/rows`, { applicationId: input.applicationId, version: input.version });
        await settle(queryClient);
        return created.interviewId;
      } catch (error) {
        show({ tone: 'danger', message: `${input.candidateName}: not added`, detail: errorMessage(error) });
        return null;
      } finally {
        setIsAdding(false);
      }
    },
    [queryClient, jobId, show],
  );
  return { add, isAdding };
}
